import { logger } from '../../utils/logger';
import { useEffect, useMemo, useState } from 'react';
import { PercentIcon, SaveIcon, DownloadIcon, FileTextIcon, SettingsIcon, ChevronDownIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Tabs } from '../ui/Tabs';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Pagination } from '../ui/Pagination';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
import { usePagination } from '../../hooks/usePagination';
import { supabase } from '../../utils/supabase';
import { fetchAllRows } from '../../utils/fetchAllRows';
import { formatCurrency } from '../../utils/helpers';
import { useToast } from '../../contexts/ToastContext';
// Restricción de fecha para ofertas en comisiones ELIMINADA: ahora cualquier
// descuento guardado en service_discounts se aplica en comisiones sin importar
// la fecha de la cita.
// ── EXPORT PDF ────────────────────────────────────────────────────────────────

// El total es la única cifra que importa en esta pantalla, así que se anima
// al cargar para llevar la vista directo a ella. Se desactiva por completo si
// el sistema pide movimiento reducido.
function useCountUp(target: number, duration = 1100) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const prefiereMenosMovimiento = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefiereMenosMovimiento || !target) {
      setValue(target);
      return;
    }
    let frame = 0;
    const inicio = performance.now();
    const paso = (ahora: number) => {
      const t = Math.min(1, (ahora - inicio) / duration);
      const suavizado = 1 - Math.pow(1 - t, 4);
      setValue(target * suavizado);
      if (t < 1) frame = requestAnimationFrame(paso);
    };
    frame = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return value;
}
const origenLabel = (r: any): string => r.origen || (r.offerPercent > 0 ? `Oferta -${r.offerPercent}%` : '');
function exportCommissionsToPdf(aptRows: any[], productRows: any[], dateFrom: string, dateTo: string, totalServices: number, totalProducts: number, totalGeneral: number) {
  const fmt = (n: number) => n.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP'
  });
  const today = new Date().toLocaleDateString('es-DO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const aptTableRows = aptRows.map((r, i) => `
      <tr class="${i % 2 === 0 ? 'even' : ''}">
        <td>${r.date}</td>
        <td>${r.patient}</td>
        <td>${r.doctor}</td>
        <td>${r.service}</td>
        <td class="center">${(() => {
    const o = origenLabel(r);
    if (!o) return '-';
    const cls = o === 'Certificado de regalo' ? 'badge-purple' : o === 'Tarjeta de fidelidad' ? 'badge-pink' : 'badge-green';
    return `<span class="badge ${cls}">${o}</span>`;
  })()}</td>
        <td class="right">${fmt(r.total)}<br><span style="font-size:8px;color:#9ca3af;text-transform:uppercase;">${r.baseLabel || 'Ganancia'}</span></td>
        <td class="right amount">${fmt(r.commission)}</td>
        <td class="center"><span class="badge ${r.type === 'Doctor' ? 'badge-green' : 'badge-blue'}">${r.type}</span></td>
        <td>${r.userName}</td>
      </tr>`).join('');
  const prodTableRows = productRows.map((r, i) => `
      <tr class="${i % 2 === 0 ? 'even' : ''}">
        <td>${r.date}</td>
        <td>${r.product}</td>
        <td class="center">${r.offerPercent > 0 ? `<span class="badge badge-green">Oferta -${r.offerPercent}%</span>` : '-'}</td>
        <td class="right">${fmt(r.total)}</td>
        <td class="right amount">${fmt(r.commission)}</td>
        <td>${r.userName}</td>
      </tr>`).join('');
  const cssText = ['* { margin: 0; padding: 0; box-sizing: border-box; }', 'body { font-family: Arial, sans-serif; font-size: 10px; color: #1f2937; background: #fff; }', '.header { background: linear-gradient(135deg, #0d9488, #0f766e); color: white; padding: 28px 36px 22px; }', '.header h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }', '.header p { font-size: 10px; opacity: 0.85; }', '.header-meta { display: flex; gap: 24px; margin-top: 12px; }', '.header-meta span { background: rgba(255,255,255,0.15); border-radius: 20px; padding: 3px 12px; font-size: 9px; font-weight: 600; }', '.summary { display: flex; gap: 12px; padding: 20px 36px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }', '.summary-card { flex: 1; background: white; border-radius: 10px; padding: 14px 18px; border-left: 4px solid #0d9488; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }', '.summary-card.purple { border-left-color: #7c3aed; }', '.summary-card.pink { border-left-color: #db2777; }', '.summary-card label { display: block; font-size: 8.5px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }', '.summary-card strong { font-size: 15px; font-weight: 700; color: #111827; }', '.section { padding: 18px 36px 0; }', '.section-title { font-size: 11px; font-weight: 700; color: #0f766e; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #0d9488; display: flex; align-items: center; gap: 6px; }', '.section-title::before { content: ""; display: inline-block; width: 8px; height: 8px; background: #0d9488; border-radius: 50%; }', 'table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }', 'thead tr { background: #134e4a; color: white; }', 'thead th { padding: 8px 10px; text-align: left; font-size: 8.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }', 'thead th.right { text-align: right; }', 'thead th.center { text-align: center; }', 'tbody td { padding: 7px 10px; font-size: 9.5px; color: #374151; border-bottom: 1px solid #f1f5f9; }', 'tbody tr.even td { background: #f8fafc; }', 'td.right { text-align: right; }', 'td.center { text-align: center; }', 'td.amount { color: #059669; font-weight: 700; }', '.badge { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 8px; font-weight: 600; }', '.badge-green { background: #d1fae5; color: #065f46; }', '.badge-blue { background: #dbeafe; color: #1e40af; }', '.badge-purple { background: #ede9fe; color: #5b21b6; }', '.badge-pink { background: #fce7f3; color: #9d174f; }', '.empty-row td { text-align: center; color: #9ca3af; font-style: italic; padding: 16px; }', '.footer { margin-top: 24px; background: #134e4a; color: rgba(255,255,255,0.7); padding: 10px 36px; display: flex; justify-content: space-between; font-size: 8px; }', '.footer span { color: white; font-weight: 600; }', '@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } @page { margin: 10mm; size: A4 landscape; } }'].join('\n');
  const styleTag = '<' + 'style' + '>' + cssText + '<' + '/style' + '>';
  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Comisiones</title>
  ${styleTag}
</head>
<body>
  <div class="header">
    <h1>Reporte de Comisiones</h1>
    <p>Savidna - Centro de Bienestar y Belleza</p>
    <div class="header-meta">
      <span>Período: ${dateFrom} — ${dateTo}</span>
      <span>${aptRows.length} comisiones de citas</span>
      <span>${productRows.length} comisiones de productos</span>
      <span>${today}</span>
    </div>
  </div>
  <div class="summary">
    <div class="summary-card">
      <label>Comisión Servicios</label>
      <strong>${fmt(totalServices)}</strong>
    </div>
    <div class="summary-card purple">
      <label>Comisión Productos</label>
      <strong>${fmt(totalProducts)}</strong>
    </div>
    <div class="summary-card pink">
      <label>Total General</label>
      <strong>${fmt(totalGeneral)}</strong>
    </div>
  </div>
  <div class="section">
    <div class="section-title">Comisiones por Citas</div>
    <table>
      <thead>
        <tr>
          <th>Fecha</th><th>Paciente</th><th>Doctor</th><th>Servicio</th>
          <th class="center">Origen</th>
          <th class="right">Base</th><th class="right">Comisión</th>
          <th class="center">Tipo</th><th>Usuario</th>
        </tr>
      </thead>
      <tbody>
        ${aptTableRows || '<tr class="empty-row"><td colspan="9">Sin registros en este período</td></tr>'}
      </tbody>
    </table>
  </div>
  <div class="section">
    <div class="section-title">Comisiones por Productos</div>
    <table>
      <thead>
        <tr>
          <th>Fecha</th><th>Producto</th>
          <th class="center">Oferta</th>
          <th class="right">Venta</th><th class="right">Comisión</th><th>Usuario</th>
        </tr>
      </thead>
      <tbody>
        ${prodTableRows || '<tr class="empty-row"><td colspan="6">Sin registros en este período</td></tr>'}
      </tbody>
    </table>
  </div>
  <div class="footer">
    <span>Savidna - Documento Confidencial</span>
    <span>Reporte de Comisiones</span>
    <span>${today}</span>
  </div>
</body>
</html>`;
  // ── FIX: abrir en ventana nueva e imprimir como PDF ───────────────────────
  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
    }, 600);
  }
}
// ── EXPORT EXCEL (CSV con estilos via HTML) ───────────────────────────────────
function exportCommissionsToExcel(aptRows: any[], productRows: any[], dateFrom: string, dateTo: string) {
  const fmt = (n: number) => n.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP'
  });
  const totalSvc = aptRows.reduce((a, b) => a + b.commission, 0);
  const totalProd = productRows.reduce((a, b) => a + b.commission, 0);
  const totalGen = totalSvc + totalProd;
  const thStyle = `background:#134e4a;color:white;padding:6px 10px;font-size:11px;font-weight:bold;border:1px solid #0d9488;`;
  const tdStyle = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;`;
  const tdAmt = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;color:#059669;font-weight:bold;text-align:right;`;
  const tdRight = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;text-align:right;`;
  const tdEven = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;background:#f8fafc;`;
  const tdEvenR = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;background:#f8fafc;text-align:right;`;
  const tdEvenA = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;background:#f8fafc;color:#059669;font-weight:bold;text-align:right;`;
  const aptTr = aptRows.map((r, i) => {
    const e = i % 2 === 0;
    return `<tr>
      <td style="${e ? tdEven : tdStyle}">${r.date}</td>
      <td style="${e ? tdEven : tdStyle}">${r.patient}</td>
      <td style="${e ? tdEven : tdStyle}">${r.doctor}</td>
      <td style="${e ? tdEven : tdStyle}">${r.service}</td>
      <td style="${e ? tdEven : tdStyle};text-align:center;">${origenLabel(r) || '-'}</td>
      <td style="${e ? tdEvenR : tdRight}">${fmt(r.total)}</td>
      <td style="${e ? tdEven : tdStyle}">${r.baseLabel || 'Ganancia'}</td>
      <td style="${e ? tdEvenA : tdAmt}">${fmt(r.commission)}</td>
      <td style="${e ? tdEven : tdStyle}">${r.type}</td>
      <td style="${e ? tdEven : tdStyle}">${r.userName}</td>
    </tr>`;
  }).join('');
  const prodTr = productRows.map((r, i) => {
    const e = i % 2 === 0;
    return `<tr>
      <td style="${e ? tdEven : tdStyle}">${r.date}</td>
      <td style="${e ? tdEven : tdStyle}">${r.product}</td>
      <td style="${e ? tdEven : tdStyle};text-align:center;">${r.offerPercent > 0 ? 'Oferta -' + r.offerPercent + '%' : '-'}</td>
      <td style="${e ? tdEvenR : tdRight}">${fmt(r.total)}</td>
      <td style="${e ? tdEvenA : tdAmt}">${fmt(r.commission)}</td>
      <td style="${e ? tdEven : tdStyle}">${r.userName}</td>
    </tr>`;
  }).join('');
  const html = `
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="UTF-8">
  <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets>
    <x:ExcelWorksheet><x:Name>Comisiones</x:Name>
    <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
    </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body>
<table style="font-family:Arial,sans-serif;border-collapse:collapse;width:100%;">
  <tr>
    <td colspan="9" style="background:#0d9488;color:white;font-size:16px;font-weight:bold;padding:14px 16px;">
      Reporte de Comisiones — ${dateFrom} al ${dateTo}
    </td>
  </tr>
  <tr>
    <td colspan="9" style="background:#134e4a;color:rgba(255,255,255,0.8);font-size:10px;padding:6px 16px;">
      Savidna - Centro de Bienestar y Belleza
    </td>
  </tr>
  <tr><td colspan="9" style="padding:6px;"></td></tr>
  <tr>
    <td colspan="2" style="background:#ecfdf5;border:1px solid #6ee7b7;padding:10px 14px;">
      <div style="font-size:9px;color:#6b7280;text-transform:uppercase;">Comisión Servicios</div>
      <div style="font-size:15px;font-weight:bold;color:#059669;">${fmt(totalSvc)}</div>
    </td>
    <td colspan="2" style="background:#f5f3ff;border:1px solid #c4b5fd;padding:10px 14px;">
      <div style="font-size:9px;color:#6b7280;text-transform:uppercase;">Comisión Productos</div>
      <div style="font-size:15px;font-weight:bold;color:#7c3aed;">${fmt(totalProd)}</div>
    </td>
    <td colspan="2" style="background:#fdf2f8;border:1px solid #f9a8d4;padding:10px 14px;">
      <div style="font-size:9px;color:#6b7280;text-transform:uppercase;">Total General</div>
      <div style="font-size:15px;font-weight:bold;color:#db2777;">${fmt(totalGen)}</div>
    </td>
    <td colspan="2" style="padding:0;"></td>
  </tr>
  <tr><td colspan="9" style="padding:8px;"></td></tr>
  <tr>
    <td colspan="9" style="background:#0f766e;color:white;font-size:11px;font-weight:bold;padding:8px 10px;text-transform:uppercase;letter-spacing:0.5px;">
      Comisiones por Citas (${aptRows.length} registros)
    </td>
  </tr>
  <tr>
    <th style="${thStyle}">Fecha</th>
    <th style="${thStyle}">Paciente</th>
    <th style="${thStyle}">Doctor</th>
    <th style="${thStyle}">Servicio</th>
    <th style="${thStyle};text-align:center;">Origen</th>
    <th style="${thStyle};text-align:right;">Base</th>
    <th style="${thStyle}">Tipo Base</th>
    <th style="${thStyle};text-align:right;">Comisión</th>
    <th style="${thStyle}">Tipo</th>
    <th style="${thStyle}">Usuario</th>
  </tr>
  ${aptTr || `<tr><td colspan="9" style="${tdStyle}color:#9ca3af;font-style:italic;">Sin registros</td></tr>`}
  <tr><td colspan="9" style="padding:8px;"></td></tr>
  <tr>
    <td colspan="9" style="background:#0f766e;color:white;font-size:11px;font-weight:bold;padding:8px 10px;text-transform:uppercase;letter-spacing:0.5px;">
      Comisiones por Productos (${productRows.length} registros)
    </td>
  </tr>
  <tr>
    <th style="${thStyle}">Fecha</th>
    <th style="${thStyle}">Producto</th>
    <th style="${thStyle};text-align:center;">Oferta</th>
    <th style="${thStyle};text-align:right;">Venta</th>
    <th style="${thStyle};text-align:right;">Comisión</th>
    <th style="${thStyle}">Usuario</th>
    <td colspan="4"></td>
  </tr>
  ${prodTr || `<tr><td colspan="6" style="${tdStyle}color:#9ca3af;font-style:italic;">Sin registros</td></tr>`}
</table>
</body>
</html>`;
  const blob = new Blob(['\uFEFF' + html], {
    type: 'application/vnd.ms-excel;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `comisiones_${dateFrom}_${dateTo}.xls`;
  a.click();
  URL.revokeObjectURL(url);
}
// ── NORMALIZE ────────────────────────────────────────────────────────────────
function normalizeName(name: string) {
  if (!name) return '';
  return name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^(dr\.|dra\.|lic\.|ing\.|ter\.)\s*/g, '').replace(/\s+/g, ' ');
}
// ── COMPONENTE ───────────────────────────────────────────────────────────────
export function Commissions() {
  const {
    showToast
  } = useToast();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [sales, setSales] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [servicePercents, setServicePercents] = useState<Record<string, string>>({});
  const [productPercents, setProductPercents] = useState<Record<string, string>>({});
  const [showConfig, setShowConfig] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  // Fecha local (no UTC). Con toISOString(), a partir de las 8:00 p.m. en
  // República Dominicana (UTC-4) el día "de hoy" saltaba al siguiente.
  const toLocalISODate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const now = new Date();
  const today = toLocalISODate(now);
  const firstDay = toLocalISODate(new Date(now.getFullYear(), now.getMonth(), 1));
  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(today);
  const [selectedUser, setSelectedUser] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  useEffect(() => {
    loadAll();
  }, []);

  // ─── Realtime: recargar si otro usuario cambia comisiones/ventas/citas ──
  useEffect(() => {
    const channel = supabase.channel('commissions-realtime').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'commission_configs'
    }, () => loadAll()).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'product_sales'
    }, () => loadAll()).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'appointments'
    }, () => loadAll()).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);
  function buildPeopleList(usersData: any[], doctorsData: any[]) {
    const allowedRoles = new Set(['doctor', 'doctora', 'terapeuta', 'terapista', 'recepcion', 'recepción', 'recepcionista', 'callcenter', 'call center', 'call_center']);
    const people: any[] = [];
    const seenNormalizedNames = new Set<string>();
    usersData.forEach((u: any) => {
      if (!allowedRoles.has((u.role || '').toLowerCase())) return;
      // Exclude inactive or archived users
      const userStatus = (u.status || 'active').toLowerCase();
      if (userStatus === 'inactive' || userStatus === 'archived') return;
      const norm = normalizeName(u.name);
      if (norm && !seenNormalizedNames.has(norm)) {
        seenNormalizedNames.add(norm);
        people.push({
          id: u.id,
          name: u.name,
          normalized: norm
        });
      }
    });
    doctorsData.forEach((d: any) => {
      const norm = normalizeName(d.name);
      if (norm && !seenNormalizedNames.has(norm)) {
        seenNormalizedNames.add(norm);
        people.push({
          id: d.id,
          name: d.name,
          normalized: norm
        });
      }
    });
    return people;
  }
  async function loadAll() {
    setLoading(true);
    try {
      const [configs, patientsData, doctorsData, usersData, servicesData, appointmentsData, salesData, productsData] = await Promise.all([fetchAllRows('commission_configs'), fetchAllRows('patients'), fetchAllRows('doctors'), fetchAllRows('users', {
        select: 'id, name, role, status'
      }), fetchAllRows('services'), fetchAllRows('appointments', {
        select: '*, patients:patient_id(id, name), doctors_rel:doctor_id(id, name), services_rel:service_id(id, name, price)'
      }), fetchAllRows('product_sales'), fetchAllRows('products', {
        select: 'id, name'
      })]);
      const allPeople = buildPeopleList(usersData, doctorsData);
      const sp: Record<string, string> = {};
      const pp: Record<string, string> = {};
      const configByNormName: Record<string, {
        service: string;
        product: string;
      }> = {};
      const configById: Record<string, {
        service: string;
        product: string;
      }> = {};
      configs.forEach((row: any) => {
        const norm = normalizeName(row.person_name);
        const pid = String(row.person_id || '');
        const scope = row.commission_scope || 'service';
        const val = String(row.commission_percent || 0);
        if (norm) {
          if (!configByNormName[norm]) configByNormName[norm] = {
            service: '0',
            product: '0'
          };
          if (scope === 'service') configByNormName[norm].service = val;
          if (scope === 'product') configByNormName[norm].product = val;
        }
        if (pid) {
          if (!configById[pid]) configById[pid] = {
            service: '0',
            product: '0'
          };
          if (scope === 'service') configById[pid].service = val;
          if (scope === 'product') configById[pid].product = val;
        }
      });
      allPeople.forEach((p: any) => {
        const byId = configById[String(p.id)];
        const byName = configByNormName[normalizeName(p.name)];
        const found = byId || byName;
        sp[p.id] = found?.service || '0';
        pp[p.id] = found?.product || '0';
      });
      setUsers(allPeople);
      setServicePercents(sp);
      setProductPercents(pp);
      setPatients(patientsData);
      setDoctors(doctorsData);
      setServices(servicesData);
      setAppointments(appointmentsData);
      setSales(salesData);
      setProducts(productsData);
    } catch (err: any) {
      logger.error('Error loading commissions:', err);
      // Antes el error se tragaba en silencio y el módulo se veía vacío,
      // como si no hubiera trabajo registrado.
      showToast({
        type: 'error',
        title: 'Error al cargar comisiones',
        message: err?.message || 'No se pudieron cargar los datos. Revisa tu conexión e inténtalo de nuevo.'
      });
    } finally {
      setLoading(false);
    }
  }
  async function saveAll() {
    try {
      const rows: any[] = [];
      users.forEach((u: any) => {
        rows.push({
          person_id: u.id,
          person_name: u.name,
          person_type: 'usuario',
          commission_scope: 'service',
          commission_percent: Number(servicePercents[u.id] || 0),
          is_active: true
        });
        rows.push({
          person_id: u.id,
          person_name: u.name,
          person_type: 'usuario',
          commission_scope: 'product',
          commission_percent: Number(productPercents[u.id] || 0),
          is_active: true
        });
      });
      if (rows.length > 0) {
        const {
          error: upsertError
        } = await supabase.from('commission_configs').upsert(rows, {
          onConflict: 'person_id,commission_scope'
        });
        if (upsertError) {
          showToast({
            type: 'error',
            title: 'Error al guardar',
            message: upsertError.message
          });
          setSaveMsg(`❌ Error: ${upsertError.message}`);
          return;
        }
      }
      showToast({
        type: 'success',
        title: 'Comisiones guardadas',
        message: 'Las comisiones fueron guardadas correctamente.'
      });
      setSaveMsg('✅ Guardado correctamente ✓');
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Error al guardar',
        message: err.message || 'No se pudieron guardar las comisiones.'
      });
      setSaveMsg(`❌ Error al guardar: ${err.message || 'Error desconocido'}`);
    }
    setTimeout(() => setSaveMsg(''), 3000);
    await loadAll();
  }
  const appointmentRows = useMemo(() => {
    return appointments.filter((x: any) => x.status === 'completed' || x.status === 'completada').flatMap((apt: any) => {
      const rows = [];
      const doctorObj = doctors.find((d: any) => String(d.id) === String(apt.doctor_id));
      const doctorName = apt.doctors_rel?.name || doctorObj?.name || apt.doctor_name || apt.doctorName || 'Doctor';
      const normDoctorName = normalizeName(doctorName);
      const commissionPersonDoc = users.find((u: any) => String(u.id) === String(apt.doctor_id) || normalizeName(u.name) === normDoctorName);
      const docCommissionId = commissionPersonDoc?.id || apt.doctor_id;
      const docPercent = Number(servicePercents[docCommissionId] || 0);
      const rawUserId = apt.created_by_user_id || apt.user_id;
      const rawUserName = apt.created_by_user_name || 'Usuario';
      const normUserName = normalizeName(rawUserName);
      const commissionPersonUser = users.find((u: any) => String(u.id) === String(rawUserId) || normalizeName(u.name) === normUserName);
      const userCommissionId = commissionPersonUser?.id || rawUserId;
      const userPercent = Number(servicePercents[userCommissionId] || 0);
      const patient = apt.patients?.name || patients.find((p: any) => String(p.id) === String(apt.patient_id))?.name || apt.patient_name || apt.patientName || 'Paciente';
      const date = (apt.date || apt.created_at || '').slice(0, 10);
      // ── BUILD LIST OF INDIVIDUAL SERVICES IN THIS APPOINTMENT ─────────────
      // Commission is calculated PER SERVICE, not over the appointment total.
      // Each service tracks BOTH bases:
      //   - precioVenta: full sale price → used for the person who COMPLETES the cita (Gestión).
      //   - ganancia: profit (price - cost) → used for the DOCTOR / terapeuta, as before.
      const serviceList: {
        name: string;
        precioVenta: number;
        ganancia: number;
        offerPercent: number;
        origen: string;
        valorRegalado: number;
      }[] = [];
      const discountsMap = apt.service_discounts || {};
      // Marcas de la cita: canje de fidelidad y pago con certificado de regalo.
      // Se leen directamente de la cita, no de service_discounts, para no
      // depender de que el descuento se haya guardado correctamente.
      const loyaltySvcId = apt.loyalty_service_id || apt.loyaltyServiceId || null;
      const giftCertId = apt.gift_certificate_id || apt.giftCertificateId || null;
      // ── DESCUENTO POR PAQUETE DE SESIONES ────────────────────────────────
      // El módulo de Citas recalcula este descuento EN VIVO cuando la cita no
      // lo tiene guardado en service_discounts (pasa, por ejemplo, si la cita
      // se agendó antes de comprar el paquete). Comisiones solo leía el mapa
      // guardado, así que mostraba el precio de lista completo mientras la
      // cita sí enseñaba el descuento. Aquí se replica el mismo cálculo.
      const patientObj = patients.find((pt: any) => String(pt.id) === String(apt.patient_id));
      const patientPackages: any[] = patientObj?.session_packages || patientObj?.sessionPackages || [];
      const getPackageDiscount = (sId: any): number | undefined => {
        if (!sId) return undefined;
        const candidatos = patientPackages.filter((pk: any) => String(pk.serviceId ?? pk.service_id) === String(sId) && (!pk.createdAt || String(pk.createdAt).slice(0, 10) <= date));
        if (candidatos.length === 0) return undefined;
        // Si el paciente completó un paquete y compró otro, se toma el MÁS
        // RECIENTE comprado antes de la cita. Buscar el primero de la lista
        // devolvía el paquete viejo y con él su descuento equivocado.
        candidatos.sort((a: any, b: any) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
        const pk = candidatos[0];
        return Number(pk.discountPercent ?? 15);
      };
      // Etiqueta de origen del descuento, para identificarlo en el reporte.
      const buildOrigen = (sId: string | undefined, offerPercent: number, soloUnServicio: boolean): string => {
        if (loyaltySvcId && sId && String(loyaltySvcId) === String(sId)) {
          return 'Tarjeta de fidelidad';
        }
        if (giftCertId) {
          // El certificado cubre el servicio canjeado. Si la cita tiene un solo
          // servicio, es ese; si tiene varios, es el que quedó con descuento
          // guardado al canjear.
          const cubierto = soloUnServicio || sId !== undefined && discountsMap[sId] !== undefined;
          if (cubierto) return 'Certificado de regalo';
        }
        if (sId !== undefined && getPackageDiscount(sId) !== undefined) {
          return `Paquete de sesiones -${offerPercent}%`;
        }
        if (offerPercent > 0) return `Oferta -${offerPercent}%`;
        return '';
      };
      // Porcentaje efectivo: el guardado en la cita o, si no hay, el del
      // paquete activo, igual que hace el módulo de Citas.
      const efectivoPercent = (sId: any): number => {
        const guardado = discountsMap[sId];
        if (guardado !== undefined && guardado !== null) return Number(guardado);
        return Number(getPackageDiscount(sId) ?? 0);
      };
      if (apt.service_ids && Array.isArray(apt.service_ids) && apt.service_ids.length > 0) {
        // Multiple services: one entry per service
        const soloUno = apt.service_ids.length === 1;
        apt.service_ids.forEach((sId: string) => {
          const s = services.find((sv: any) => String(sv.id) === String(sId));
          if (s) {
            const basePrice = Number(s.price || 0);
            const offerPercent = efectivoPercent(sId);
            const origen = buildOrigen(sId, offerPercent, soloUno);
            // Canje de fidelidad: el servicio fue gratis PARA EL CLIENTE, pero
            // no para la clínica. La persona que lo realizó cobra su comisión
            // sobre el precio completo, así que se ignora el 100% de descuento
            // que el canje guarda en service_discounts.
            const esFidelidad = origen === 'Tarjeta de fidelidad';
            const price = esFidelidad ? basePrice : offerPercent > 0 ? Math.max(0, basePrice - basePrice * (offerPercent / 100)) : basePrice;
            const cost = Number(s.cost || 0);
            serviceList.push({
              name: s.name,
              precioVenta: price,
              // Valor que el cliente no pagó. En fidelidad es el precio
              // completo; en certificados, la parte descontada.
              valorRegalado: origen === 'Tarjeta de fidelidad' ? basePrice : origen === 'Certificado de regalo' ? Math.max(0, basePrice - price) : 0,
              // La ganancia nunca es negativa: si el servicio se regaló, el
              // costo no se le descuenta de la comisión a la persona.
              ganancia: cost > 0 ? Math.max(0, price - cost) : price,
              offerPercent,
              origen
            });
          }
        });
      }
      if (serviceList.length === 0) {
        // Single service (backward compatibility) or services not found in array
        const service = apt.services_rel || services.find((s: any) => String(s.id) === String(apt.service_id));
        const name = service?.name || apt.service_name || apt.serviceName || 'Servicio';
        const basePrice = Number(service?.price || apt.total || apt.amount || 0);
        const offerPercent = efectivoPercent(apt.service_id);
        const origen = buildOrigen(apt.service_id, offerPercent, true);
        // Ver nota arriba: fidelidad comisiona sobre el precio completo.
        const esFidelidad = origen === 'Tarjeta de fidelidad';
        const price = esFidelidad ? basePrice : offerPercent > 0 ? Math.max(0, basePrice - basePrice * (offerPercent / 100)) : basePrice;
        const cost = Number(service?.cost || 0);
        serviceList.push({
          name,
          precioVenta: price,
          valorRegalado: origen === 'Tarjeta de fidelidad' ? basePrice : origen === 'Certificado de regalo' ? Math.max(0, basePrice - price) : 0,
          ganancia: cost > 0 ? Math.max(0, price - cost) : price,
          offerPercent,
          origen
        });
      }
      // ── ONE COMMISSION ROW PER SERVICE ────────────────────────────────────
      serviceList.forEach((svc, svcIdx) => {
        // Doctor / terapeuta → commission over PROFIT (price - cost).
        // Person who completes the cita (Gestión) → commission over FULL sale price.
        const baseDoctor = svc.ganancia;
        const baseVenta = svc.precioVenta;
        if (docPercent > 0) {
          rows.push({
            id: `${apt.id}-svc${svcIdx}-doc`,
            date,
            patient,
            doctor: doctorName,
            service: svc.name,
            total: baseDoctor,
            baseLabel: 'Ganancia',
            commission: baseDoctor * (docPercent / 100),
            userName: commissionPersonDoc?.name || doctorName,
            userId: docCommissionId,
            type: 'Doctor',
            offerPercent: svc.offerPercent,
            origen: svc.origen,
            valorRegalado: svc.valorRegalado
          });
        }
        if (userPercent > 0 && userCommissionId !== docCommissionId) {
          rows.push({
            id: `${apt.id}-svc${svcIdx}-user`,
            date,
            patient,
            doctor: doctorName,
            service: svc.name,
            total: baseVenta,
            baseLabel: 'Precio venta',
            commission: baseVenta * (userPercent / 100),
            userName: commissionPersonUser?.name || rawUserName,
            userId: userCommissionId,
            type: 'Gestión',
            offerPercent: svc.offerPercent,
            origen: svc.origen,
            valorRegalado: svc.valorRegalado
          });
        }
        if (docPercent <= 0 && !(userPercent > 0 && userCommissionId !== docCommissionId)) {
          rows.push({
            id: `${apt.id}-svc${svcIdx}`,
            date,
            patient,
            doctor: doctorName,
            service: svc.name,
            total: baseDoctor,
            baseLabel: 'Ganancia',
            commission: 0,
            offerPercent: svc.offerPercent,
            origen: svc.origen,
            valorRegalado: svc.valorRegalado,
            userName: doctorName,
            userId: docCommissionId,
            type: 'Servicio'
          });
        }
      });
      return rows;
    }).filter((x: any) => {
      if (x.date < dateFrom) return false;
      if (x.date > dateTo) return false;
      if (selectedUser !== 'all' && String(x.userId) !== String(selectedUser)) return false;
      return true;
    });
  }, [appointments, patients, doctors, services, users, servicePercents, dateFrom, dateTo, selectedUser]);
  const productRows = useMemo(() => {
    const productNameMap: Record<string, string> = {};
    products.forEach((p: any) => {
      productNameMap[String(p.id)] = p.name;
    });
    return sales.flatMap((sale: any) => {
      const rawUserId = sale.created_by_user_id || sale.user_id;
      const rawUserName = sale.created_by_user_name || 'Usuario';
      const normName = normalizeName(rawUserName);
      const commissionPerson = users.find((u: any) => String(u.id) === String(rawUserId) || normalizeName(u.name) === normName);
      const commissionId = commissionPerson?.id || rawUserId;
      const userName = commissionPerson?.name || rawUserName;
      const percent = Number(productPercents[commissionId] || 0);
      const date = (sale.date || sale.created_at || '').slice(0, 10);
      const items = Array.isArray(sale.items) ? sale.items : [];
      if (items.length === 0) {
        const total = Number(sale.total || 0);
        return [{
          id: sale.id,
          date,
          product: 'Venta de productos',
          total,
          commission: total * (percent / 100),
          userName,
          userId: commissionId
        }];
      }
      return items.map((item: any, idx: number) => {
        const productName = productNameMap[String(item.productId)] || item.productName || 'Producto';
        const subtotal = Number(item.subtotal || (item.unitPrice || 0) * (item.quantity || 0));
        return {
          id: `${sale.id}-${idx}`,
          date,
          product: `${productName} (x${item.quantity || 1})`,
          total: subtotal,
          commission: subtotal * (percent / 100),
          userName,
          userId: commissionId,
          offerPercent: Number(item.discount || 0)
        };
      });
    }).filter((x: any) => {
      if (x.date < dateFrom) return false;
      if (x.date > dateTo) return false;
      if (selectedUser !== 'all' && String(x.userId) !== String(selectedUser)) return false;
      return true;
    });
  }, [sales, products, users, productPercents, dateFrom, dateTo, selectedUser]);
  const totalServices = appointmentRows.reduce((a, b) => a + b.commission, 0);
  const totalProducts = productRows.reduce((a, b) => a + b.commission, 0);
  const totalGeneral = totalServices + totalProducts;
  const totalAnimado = useCountUp(totalGeneral);
  const handleExportToExcel = async (exportDateFrom: string | null, exportDateTo: string | null) => {
    setIsExporting(true);
    await new Promise(r => setTimeout(r, 400));
    const from = exportDateFrom ?? dateFrom;
    const to = exportDateTo ?? dateTo;
    const filteredApt = exportDateFrom ? appointmentRows.filter(r => r.date >= from && r.date <= to) : appointmentRows;
    const filteredProd = exportDateFrom ? productRows.filter(r => r.date >= from && r.date <= to) : productRows;
    exportCommissionsToExcel(filteredApt, filteredProd, from, to);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (exportDateFrom: string | null, exportDateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(r => setTimeout(r, 400));
    const from = exportDateFrom ?? dateFrom;
    const to = exportDateTo ?? dateTo;
    const filteredApt = exportDateFrom ? appointmentRows.filter(r => r.date >= from && r.date <= to) : appointmentRows;
    const filteredProd = exportDateFrom ? productRows.filter(r => r.date >= from && r.date <= to) : productRows;
    const tSvc = filteredApt.reduce((a, b) => a + b.commission, 0);
    const tProd = filteredProd.reduce((a, b) => a + b.commission, 0);
    exportCommissionsToPdf(filteredApt, filteredProd, from, to, tSvc, tProd, tSvc + tProd);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const configPagination = usePagination(users, {
    itemsPerPage: 8
  });
  const aptPagination = usePagination(appointmentRows, {
    itemsPerPage: 10
  });
  const productPagination = usePagination(productRows, {
    itemsPerPage: 10
  });
  if (loading) return <div className="p-10 text-center text-gray-500 dark:text-gray-400 animate-pulse" data-id="element-2064" data-mp="1att5er3ttujj">Cargando comisiones...</div>;
  return <div className="space-y-6" data-id="element-2065" data-mp="10xpyl7y157q9">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2066" data-mp="10w51pbo8tcp8">
        <div className="flex items-center gap-3.5" data-id="element-2067" data-mp="vzemsro6cn5e">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-teal-50 dark:bg-teal-500/10 ring-1 ring-teal-100 dark:ring-teal-500/20" data-id="element-2068" data-mp="rx1je9wwh1bs">
            <PercentIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" data-id="element-2069" data-mp="2qbcsijn9z0wj" />
          </span>
          <div data-id="element-2070" data-mp="2mcdt1ekbbwch">
            <h1 className="text-2xl md:text-[28px] font-semibold tracking-tight text-slate-900 dark:text-white leading-none" data-id="element-2071" data-mp="2mbu95sb3qxvt">
              Comisiones
            </h1>
            <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400" data-id="element-2072" data-mp="35sbramqlbwgf">
              {dateFrom} — {dateTo}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center" data-id="element-2073" data-mp="2vv73kih3jsxi">
          <Button variant="outline" leftIcon={<SettingsIcon className="w-4 h-4" data-id="element-2075" data-mp="3tjh6r0smcmq7" />} onClick={() => setShowConfig(v => !v)} data-id="element-2074" data-mp="2vwqledieic1e">
            Configuración
            <ChevronDownIcon className={`ml-1.5 h-3.5 w-3.5 transition-transform duration-200 ${showConfig ? 'rotate-180' : ''}`} data-id="element-2076" data-mp="3okw8h6wxgd4e" />
          </Button>
          <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-2078" data-mp="3etrr60js4g6e" />} onClick={() => setIsPdfExportModalOpen(true)} data-id="element-2077" data-mp="3eutdx2a5qr3y">
            PDF
          </Button>
          <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2080" data-mp="bc9w6tp3v3ne" />} onClick={() => setIsExportModalOpen(true)} data-id="element-2079" data-mp="bdb9l4tlg28a">
            Excel
          </Button>
        </div>
      </div>

      {saveMsg && <div className={`font-medium px-4 py-2 rounded-xl text-sm ${saveMsg.includes("❌") ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400" : "bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400"}`} data-id="element-2081" data-mp="nnkfw3b7yizu">{saveMsg}</div>}

      {/* RESUMEN */}
      <div className="relative overflow-hidden rounded-[28px] bg-slate-50 dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-800" data-id="element-2082" data-mp="xn1i2ar9ier2">
        <span className="pointer-events-none absolute -left-24 -top-32 h-64 w-64 rounded-full bg-teal-300/30 dark:bg-teal-500/10 blur-3xl" data-id="element-2083" data-mp="16m6g7fyww62p" />
        <span className="pointer-events-none absolute -right-20 -bottom-32 h-56 w-56 rounded-full bg-sky-200/40 dark:bg-sky-500/10 blur-3xl" data-id="element-2084" data-mp="1beg1ujszujz6" />

        <div className="relative px-6 py-7 md:px-10 md:py-9" data-id="element-2085" data-mp="1ge2ht107iebo">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400" data-id="element-2086" data-mp="1lcvsyms0tt8o">
            Total a pagar
          </p>
          <p className="mt-2 text-[34px] md:text-5xl font-semibold tracking-[-0.035em] text-slate-900 dark:text-white tabular-nums leading-[1]" data-id="element-2087" data-mp="1q8b6teq13p53">
            {formatCurrency(totalAnimado)}
          </p>

          {totalGeneral > 0 && <div className="mt-7 max-w-xl" data-id="element-2088" data-mp="1abtdo3plazlx">
              <div className="flex h-1.5 w-full gap-1.5" data-id="element-2089" data-mp="1admjxhwajhte">
                <div className="rounded-full bg-teal-500 transition-all duration-1000 ease-out" style={{
              width: `${totalServices / totalGeneral * 100}%`
            }} data-id="element-2090" data-mp="15ktfyw8ncdid" />
                <div className="rounded-full bg-slate-300 dark:bg-slate-700 transition-all duration-1000 ease-out" style={{
              width: `${totalProducts / totalGeneral * 100}%`
            }} data-id="element-2091" data-mp="1edx0tplmfsn1" />
              </div>
              <div className="mt-4 flex flex-wrap gap-x-9 gap-y-3" data-id="element-2092" data-mp="1obkxil2sfa7u">
                <div data-id="element-2093" data-mp="3v2amjn48sb2s">
                  <span className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400" data-id="element-2094" data-mp="3v1siw5f92uku">
                    <span className="h-2 w-2 rounded-full bg-teal-500" data-id="element-2095" data-mp="3q3zcpcdy60kk" />
                    Servicios
                  </span>
                  <p className="mt-0.5 text-base font-semibold text-slate-900 dark:text-white tabular-nums" data-id="element-2096" data-mp="25loubm0a0ejx">
                    {formatCurrency(totalServices)}
                  </p>
                </div>
                <div data-id="element-2097" data-mp="1vuu30386aorm">
                  <span className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400" data-id="element-2098" data-mp="1vtj1yb643v47">
                    <span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-slate-700" data-id="element-2099" data-mp="20s3urpj1bcim" />
                    Productos
                  </span>
                  <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white tabular-nums" data-id="element-2100" data-mp="1qxbsi8eic1hi">
                    {formatCurrency(totalProducts)}
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* FILTROS */}
      <div className="flex flex-wrap items-center gap-2.5" data-id="element-2101" data-mp="37a92bdnutspw">
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} aria-label="Desde" className="rounded-full border-0 bg-white dark:bg-slate-900 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 ring-1 ring-slate-200 dark:ring-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-2102" data-mp="378phd9r96ths" />
        <span className="text-sm text-slate-400" data-id="element-2103" data-mp="135cy5igrzt6l">a</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} aria-label="Hasta" className="rounded-full border-0 bg-white dark:bg-slate-900 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 ring-1 ring-slate-200 dark:ring-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-2104" data-mp="y7usq21yvw3c" />
        <div className="min-w-[180px]" data-id="element-2105" data-mp="jiyetod8bout">
          <Select value={selectedUser} onChange={setSelectedUser} options={[{
          value: 'all',
          label: 'Todas las personas'
        }, ...users.map((u: any) => ({
          value: u.id,
          label: u.name
        }))]} data-id="element-2106" data-mp="fhd6mgbn3yqy" />
        </div>
      </div>

      {/* CONFIG — oculto hasta pulsar el botón Configuración */}
      {showConfig && <div className="rounded-3xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/80 dark:ring-slate-800 overflow-hidden" data-id="element-2107" data-mp="2h5r7dzc7h92i">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 px-6 py-4" data-id="element-2108" data-mp="2m76qvmyqj7is">
            <div data-id="element-2109" data-mp="2v6bpetaar0uy">
              <h2 className="text-base font-semibold tracking-tight text-slate-900 dark:text-white" data-id="element-2110" data-mp="2v4quh2k2lfwk">
                Porcentajes por persona
              </h2>
              <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400" data-id="element-2111" data-mp="23emb9781qywx">
                Se aplican a las comisiones de servicios y productos.
              </p>
            </div>
            <Button leftIcon={<SaveIcon className="w-4 h-4" data-id="element-2113" data-mp="2c6xsq3l6nc0r" />} onClick={saveAll} data-id="element-2112" data-mp="2c762hmm0e17q">
              Guardar cambios
            </Button>
          </div>
          <div className="p-6" data-id="element-2114" data-mp="1foi8t36lh57i">
          {/* Mobile stacked rows */}
          <div className="sm:hidden space-y-3" data-id="element-2115" data-mp="1jqd8k38vnaj6">
            {configPagination.paginatedItems.map((u: any) => <div key={u.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-3" data-id="element-2116" data-mp="2n197i8xleelh">
                <p className="font-medium text-gray-900 dark:text-white text-sm mb-2" data-id="element-2117" data-mp="2i37qrunn2rd6">{u.name}</p>
                <div className="grid grid-cols-2 gap-3" data-id="element-2118" data-mp="2ws3uxgmkrk8i">
                  <div data-id="element-2119" data-mp="2wtoop412ez5b">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2120" data-mp="2rsiybcuvnnyy">Servicios %</p>
                    <input value={servicePercents[u.id] || ''} onChange={e => setServicePercents(prev => ({
                  ...prev,
                  [u.id]: e.target.value
                }))} className="border rounded-xl w-full text-center px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" data-id="element-2121" data-mp="242wycn6ol4pw" />
                  </div>
                  <div data-id="element-2122" data-mp="291886xwz7f84">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2123" data-mp="29445ql4ww1w1">Productos %</p>
                    <input value={productPercents[u.id] || ''} onChange={e => setProductPercents(prev => ({
                  ...prev,
                  [u.id]: e.target.value
                }))} className="border rounded-xl w-full text-center px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" data-id="element-2124" data-mp="3ogyonb5ta1d4" />
                  </div>
                </div>
              </div>)}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-2125" data-mp="3n1cnxh85k5tl">
            <table className="w-full" data-id="element-2126" data-mp="3n1utx5km9yep">
              <thead data-id="element-2127" data-mp="em2o6frqjcoz">
                <tr className="border-b border-slate-200 dark:border-slate-700" data-id="element-2128" data-mp="emuat13cu034">
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2129" data-mp="jlfb10ilwjrk">Usuario</th>
                  <th className="text-center px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2130" data-mp="4tx19kgcblgn">Servicios %</th>
                  <th className="text-center px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2131" data-mp="xjm3200vjpjb">Productos %</th>
                </tr>
              </thead>
              <tbody data-id="element-2132" data-mp="2yfjw3uo5oy17">
                {configPagination.paginatedItems.map((u: any) => <tr key={u.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-2133" data-mp="2to1tl26hipe4">
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium" data-id="element-2134" data-mp="2pmzxgu7v6tgd">{u.name}</td>
                    <td className="text-center" data-id="element-2135" data-mp="2koolody0e8vi"><input value={servicePercents[u.id] || ''} onChange={e => setServicePercents(prev => ({
                    ...prev,
                    [u.id]: e.target.value
                  }))} className="border rounded-xl w-24 text-center px-2 py-1 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500" data-id="element-2136" data-mp="2kl0tpj87puiw" /></td>
                    <td className="text-center" data-id="element-2137" data-mp="1wx9fga3437ez"><input value={productPercents[u.id] || ''} onChange={e => setProductPercents(prev => ({
                    ...prev,
                    [u.id]: e.target.value
                  }))} className="border rounded-xl w-24 text-center px-2 py-1 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500" data-id="element-2138" data-mp="1wxsvfl97blqd" /></td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <Pagination {...configPagination} data-id="element-2139" data-mp="1wjnqwzhqjg3h" />
          </div>
        </div>}

      {/* TABLAS */}
      <Tabs tabs={[{
      id: 'citas',
      label: 'Comisión por Citas',
      content: <div className="overflow-hidden" data-id="element-2141" data-mp="25jkfl144keyj">
                <div className="p-0" data-id="element-2142" data-mp="25i94eg8co4xe">
                  {/* Mobile cards */}
                  <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2143" data-mp="2akhwladef2yg">
                    {aptPagination.paginatedItems.length === 0 ? <div className="py-14 text-center" data-id="element-2144" data-mp="2ffx8hwruios8"><p className="text-sm font-medium text-slate-600 dark:text-slate-300" data-id="element-2145" data-mp="g5p7lu70tafh">Sin comisiones en este período</p><p className="mt-1 text-sm text-slate-400" data-id="element-2146" data-mp="l3gw0e8gieex">Ajusta las fechas o cambia la persona seleccionada.</p></div> : aptPagination.paginatedItems.map((row: any) => <div key={row.id} className="px-4 py-3" data-id="element-2147" data-mp="2rhqaqnrl50an">
                        <div className="flex items-start justify-between gap-2" data-id="element-2148" data-mp="2wbcpqml81g9m">
                          <div className="flex-1 min-w-0" data-id="element-2149" data-mp="2ijxu9ga4nmlo">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-2150" data-mp="2ihbn3hr5f93z">{row.patient}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-2151" data-mp="3b83hqaskfpp7">{row.doctor}</p>
                            <p className="text-xs text-gray-400 truncate" data-id="element-2152" data-mp="3f9x9tuop3ldr">{row.service} · {row.date}</p>
                            {row.origen && <Badge variant={row.origen === 'Certificado de regalo' ? 'info' : row.origen === 'Tarjeta de fidelidad' ? 'warning' : 'success'} className="text-xs mt-1" data-id="element-2153" data-mp="318kzpzw7udzs">{row.origen}</Badge>}
                          </div>
                          <div className="text-right flex-shrink-0" data-id="element-2154" data-mp="1pwk3y10xppaj">
                            <p className="text-sm font-semibold text-teal-700 dark:text-teal-400 tabular-nums" data-id="element-2155" data-mp="1uud7emaus574">{formatCurrency(row.commission)}</p>
                            <p className="text-xs text-gray-400" data-id="element-2156" data-mp="1iayg44lvpyd6">{formatCurrency(row.total)} · {row.baseLabel || 'Ganancia'}</p>
                            <Badge variant={row.type === 'Doctor' ? 'success' : 'default'} className="text-xs mt-1" data-id="element-2157" data-mp="1wazg7hubl1rl">{row.userName}</Badge>
                          </div>
                        </div>
                      </div>)}
                  </div>
                  {/* Desktop table */}
                  <div className="hidden sm:block overflow-x-auto" data-id="element-2158" data-mp="zl08fplzu7a2">
                    <table className="w-full" data-id="element-2159" data-mp="zingqkqfoijt">
                      <thead className="border-b border-slate-200 dark:border-slate-700" data-id="element-2160" data-mp="1dc53sfibwdel">
                        <tr data-id="element-2161" data-mp="18kwq379yu5vm">
                          <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2162" data-mp="18jlnvbw6566m">Fecha</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2163" data-mp="fx0y2fvcd256">Paciente</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2164" data-mp="2fwmjvy79956n">Doctor</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2165" data-mp="2ppv3ji2nlt2t">Servicio</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2166" data-mp="1x04mp19n0d30">Base</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2167" data-mp="21z7q6h9yyb8v">Comisión</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2168" data-mp="2b0p3xszwm0ue">Tipo</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2169" data-mp="3met1f95pa747">Usuario</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-2170" data-mp="16pm1ut7svfjq">
                        {aptPagination.paginatedItems.map((row: any) => <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-teal-50/50 dark:hover:bg-teal-500/5 transition-colors" data-id="element-2171" data-mp="11yvy1kf0vkr5">
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300" data-id="element-2172" data-mp="wzsu63as3kkr">{row.date}</td>
                          <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium" data-id="element-2173" data-mp="s29b8q34yjmh">{row.patient}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300" data-id="element-2174" data-mp="n47tq9lcysd4">{row.doctor}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300" data-id="element-2175" data-mp="j24f5c3e77nm">
                            <div className="flex items-center gap-2" data-id="element-2176" data-mp="e39rnwuhcux6">
                              <span data-id="element-2177" data-mp="2dczmyc6r6ipc">{row.service}</span>
                              {row.origen === 'Certificado de regalo' ? <Badge variant="info" data-id="element-2178" data-mp="28gj1jpqxuify">Certificado de regalo</Badge> : row.origen === 'Tarjeta de fidelidad' ? <Badge variant="warning" data-id="element-2179" data-mp="155lpvph7qejo">Tarjeta de fidelidad</Badge> : row.offerPercent > 0 ? <Badge variant="success" data-id="element-2180" data-mp="1a3n8k9aq8bum">Oferta -{row.offerPercent}%</Badge> : null}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right text-slate-600 dark:text-slate-300 tabular-nums" data-id="element-2181" data-mp="w5ymiy4xaynz">
                            <span data-id="element-2182" data-mp="mdsxnx9ywxd0">{formatCurrency(row.total)}</span>
                            <span className="block text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500" data-id="element-2183" data-mp="mchunzocjtmy">{row.baseLabel || 'Ganancia'}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-600 dark:text-emerald-400 font-bold" data-id="element-2184" data-mp="depcmnr8q7vv">{formatCurrency(row.commission)}</td>
                          <td className="px-4 py-3 text-center" data-id="element-2185" data-mp="3f89aczgo632"><Badge variant={row.type === 'Doctor' ? 'success' : 'default'} data-id="element-2186" data-mp="3g9kchgh8sks">{row.type || 'Servicio'}</Badge></td>
                          <td className="px-4 py-3 text-center" data-id="element-2187" data-mp="8d9ub3gg6fh6"><Badge variant="outline" data-id="element-2188" data-mp="3kxdqxr24tb8g">{row.userName}</Badge></td>
                        </tr>)}
                      </tbody>
                    </table>
                  </div>
                  <div className="p-4" data-id="element-2189" data-mp="3uvtn48g0naot"><Pagination {...aptPagination} data-id="element-2190" data-mp="3uwctcrwb0an0" /></div>
                </div>
              </div>
    }, {
      id: 'productos',
      label: 'Comisión Productos',
      content: <div className="overflow-hidden" data-id="element-2191" data-mp="3pz369iwe0b2c">
                <div className="p-0" data-id="element-2192" data-mp="7q2amrhik580">
                  {/* Mobile cards */}
                  <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2193" data-mp="2skbttx7n6dq">
                    {productPagination.paginatedItems.length === 0 ? <div className="py-14 text-center" data-id="element-2194" data-mp="hgdrtfq7kawu"><p className="text-sm font-medium text-slate-600 dark:text-slate-300" data-id="element-2195" data-mp="hirnnqsppr5g">Sin comisiones en este período</p><p className="mt-1 text-sm text-slate-400" data-id="element-2196" data-mp="qhldmybjfja1">Ajusta las fechas o cambia la persona seleccionada.</p></div> : productPagination.paginatedItems.map((row: any) => <div key={row.id} className="px-4 py-3 flex items-center justify-between gap-3" data-id="element-2197" data-mp="mhkzlescpa3i">
                        <div className="flex-1 min-w-0" data-id="element-2198" data-mp="tkrkca6zg2f1">
                          <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-2199" data-mp="yhrqel3u1565">{row.product}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2200" data-mp="13gcooezixery">{row.date} · {row.userName}</p>
                        </div>
                        <div className="text-right flex-shrink-0" data-id="element-2201" data-mp="18a777f3e7npg">
                          <p className="text-sm font-semibold text-teal-700 dark:text-teal-400 tabular-nums" data-id="element-2202" data-mp="1c8whgyt4wp4v">{formatCurrency(row.commission)}</p>
                          <p className="text-xs text-gray-400" data-id="element-2203" data-mp="1m7vrakpmyqtg">{formatCurrency(row.total)}</p>
                        </div>
                      </div>)}
                  </div>
                  {/* Desktop table */}
                  <div className="hidden sm:block overflow-x-auto" data-id="element-2204" data-mp="3nziba2b41lgf">
                    <table className="w-full" data-id="element-2205" data-mp="3o1blfrkxmnt6">
                      <thead className="border-b border-slate-200 dark:border-slate-700" data-id="element-2206" data-mp="3sxjuqocg7msf">
                        <tr data-id="element-2207" data-mp="2a3qs3qgl2j6y">
                          <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2208" data-mp="2a40el42f687w">Fecha</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2209" data-mp="21adp1vot2175">Producto</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2210" data-mp="1w9ppglmigc1b">Venta</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2211" data-mp="2p1j0zzrmk9c6">Comisión</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400" data-id="element-2212" data-mp="2f2k3ndcb804h">Usuario</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-2213" data-mp="13mvf4hjeqt1k">
                        {productPagination.paginatedItems.map((row: any) => <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-teal-50/50 dark:hover:bg-teal-500/5 transition-colors" data-id="element-2214" data-mp="33ov1go8rt0hz">
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300" data-id="element-2215" data-mp="38meh9sl49lc1">{row.date}</td>
                          <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium" data-id="element-2216" data-mp="2ttb4dyigzvrn">
                            <div className="flex items-center gap-2" data-id="element-2217" data-mp="2yse3z3fol9s8">
                              <span data-id="element-2218" data-mp="3mge3arr268if">{row.product}</span>
                              {row.offerPercent > 0 && <Badge variant="success" data-id="element-2219" data-mp="3rdxetjwdg76o">Oferta -{row.offerPercent}%</Badge>}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right text-slate-600 dark:text-slate-300 tabular-nums" data-id="element-2220" data-mp="3co89dch8xh0l">{formatCurrency(row.total)}</td>
                          <td className="px-4 py-3 text-right text-emerald-600 dark:text-emerald-400 font-bold" data-id="element-2221" data-mp="98azl4mzyb2s">{formatCurrency(row.commission)}</td>
                          <td className="px-4 py-3 text-center" data-id="element-2222" data-mp="e1x429j0b8ni"><Badge data-id="element-2223" data-mp="1yppqrkjojst8">{row.userName}</Badge></td>
                        </tr>)}
                      </tbody>
                    </table>
                  </div>
                  <div className="p-4" data-id="element-2224" data-mp="28gaypyl8u9ot"><Pagination {...productPagination} data-id="element-2225" data-mp="28gsr2k4sc3rg" /></div>
                </div>
              </div>
    }]} data-id="element-2140" data-mp="20jz6vyz5ks8m" />
      

      {/* MODALES */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Comisiones" isExporting={isExporting} data-id="element-2226" data-mp="23iiuq67bqx49" />
      
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Comisiones a PDF" isExporting={isPdfExporting} data-id="element-2227" data-mp="1atkb14njzmwu" />
      
    </div>;
}